<template>
  <v-dialog v-model="showModalRegister" max-width="620">
    <v-card id="modalRegister" :class="{ registered }">
      <v-btn
          class="modal-close"
          icon
          variant="text"
          aria-label="Registrierung schließen"
          @click="setState(false)"
      >
        <i class="fa-solid fa-xmark"/>
      </v-btn>

      <v-card-text class="pt-10 d-flex flex-column">
        <h1 class="text-center mb-5">
          {{ registered ? 'Vielen Dank' : 'Kundenkonto erstellen' }}
        </h1>

        <v-alert
            v-if="registered"
            type="success"
            variant="tonal"
            class="mb-6"
        >
          Wir haben Ihnen einen Bestätigungslink per E-Mail gesendet. Prüfen Sie bitte Ihr Postfach und den Spam-Ordner. Nach der Bestätigung können Sie sich anmelden.
        </v-alert>

        <v-alert
            v-if="errorRegister && !registered"
            class="mb-5"
            density="compact"
            type="error"
            variant="tonal"
        >
          {{ errorRegister }}
        </v-alert>

        <v-form
            v-if="!registered"
            ref="form"
            v-model="registerForm.valid"
            :disabled="loadingRegister"
            class="w-100"
            @submit.prevent="registerUser"
        >
          <p class="text-center mb-5">Alle angezeigten Felder sind Pflichtfelder.</p>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                  v-model="registerForm.customer"
                  :items="['Privat', 'Gewerblich']"
                  :rules="[rules.required]"
                  label="Kundentyp*"
                  variant="outlined"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                  v-model="registerForm.title"
                  :items="['Frau', 'Herr', 'Divers']"
                  :rules="[rules.required]"
                  label="Anrede*"
                  variant="outlined"
              />
            </v-col>

            <v-col v-if="registerForm.customer === 'Gewerblich'" cols="12">
              <v-text-field
                  v-model.trim="registerForm.company"
                  :rules="[rules.required]"
                  autocomplete="organization"
                  label="Firma*"
                  variant="outlined"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                  v-model.trim="registerForm.firstname"
                  :rules="[rules.required]"
                  autocomplete="given-name"
                  label="Vorname*"
                  variant="outlined"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                  v-model.trim="registerForm.lastname"
                  :rules="[rules.required]"
                  autocomplete="family-name"
                  label="Nachname*"
                  variant="outlined"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                  v-model="addressInput"
                  :rules="[rules.required, rules.selectedAddress]"
                  autocomplete="off"
                  label="Vollständige Adresse*"
                  variant="outlined"
                  :loading="addressLoading"
                  @update:modelValue="handleAddressInput"
              />

              <v-card
                  v-if="addressSuggestions.length"
                  class="address-suggestions mb-3"
                  elevation="8"
              >
                <v-list density="compact">
                  <v-list-item
                      v-for="suggestion in addressSuggestions"
                      :key="suggestion.placeId"
                      @click="selectAddress(suggestion)"
                  >
                    <v-list-item-title>
                      <i class="fa-solid fa-location-dot mr-2"/>{{ suggestion.label }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>

              <v-card-text class="text-medium-emphasis text-caption pa-0">
                Bitte wählen Sie eine eindeutige Adresse aus den Vorschlägen aus.
              </v-card-text>
            </v-col>

            <v-col cols="12">
              <v-text-field
                  v-model.trim="registerForm.phone"
                  :rules="[rules.required, rules.phone]"
                  autocomplete="tel"
                  label="Telefon*"
                  type="tel"
                  variant="outlined"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                  v-model.trim="registerForm.email"
                  :rules="[rules.required, rules.email]"
                  autocomplete="email"
                  label="E-Mail*"
                  type="email"
                  variant="outlined"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                  v-model="registerForm.password"
                  :rules="[rules.required, rules.password, rules.passwordMin, rules.passwordMax]"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  counter="20"
                  label="Passwort*"
                  variant="outlined"
              >
                <template v-slot:append-inner>
                  <v-btn
                      icon
                      size="small"
                      variant="text"
                      :aria-label="showPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'"
                      @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"/>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                  v-model="registerForm.passwordConfirmation"
                  :rules="[rules.required, rules.confirm]"
                  :type="showPasswordConfirmation ? 'text' : 'password'"
                  autocomplete="new-password"
                  label="Passwort wiederholen*"
                  variant="outlined"
              >
                <template v-slot:append-inner>
                  <v-btn
                      icon
                      size="small"
                      variant="text"
                      :aria-label="showPasswordConfirmation ? 'Passwort ausblenden' : 'Passwort anzeigen'"
                      @click="showPasswordConfirmation = !showPasswordConfirmation"
                  >
                    <i :class="showPasswordConfirmation ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"/>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-checkbox
                  v-model="registerForm.acceptPrivacy"
                  :rules="[rules.accepted]"
                  color="primary"
              >
                <template v-slot:label>
                  <span>
                    Ich akzeptiere die
                    <a href="" @click.stop.prevent="showLegalConditions">AGB</a>
                    und die
                    <a href="" @click.stop.prevent="showLegalPrivacy">Datenschutzerklärung</a>.
                  </span>
                </template>
              </v-checkbox>
            </v-col>

            <v-col cols="12">
              <v-btn
                  block
                  class="btn-primary"
                  type="submit"
                  :disabled="!registerForm.valid || loadingRegister"
                  :loading="loadingRegister"
              >
                <i class="fa-solid fa-user-plus mr-2"/>
                Registrieren
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <v-img
            v-if="registered"
            :src="require('@/assets/undraw_confirmed.svg')"
            max-height="220"
            contain
        />
      </v-card-text>
    </v-card>

    <Legal ref="Legal"/>
  </v-dialog>
</template>

<script>
import Legal from "@/components/modal/Legal";
import {mapGetters} from "vuex";
import {searchPhotonAddresses} from "@/services/photonAddress";

const emptyRegisterForm = () => ({
  valid: false,
  email: '',
  password: '',
  passwordConfirmation: '',
  customer: '',
  company: '',
  title: '',
  firstname: '',
  lastname: '',
  street: '',
  house: '',
  zip: '',
  place: '',
  phone: '',
  acceptPrivacy: false
})

export default {
  name: 'ModalRegister',

  components: {
    Legal
  },

  data() {
    return {
      showModalRegister: false,
      registerForm: emptyRegisterForm(),
      addressInput: '',
      selectedAddress: null,
      addressSuggestions: [],
      addressLoading: false,
      addressSearchTimeout: null,
      showPassword: false,
      showPasswordConfirmation: false
    }
  },

  computed: {
    ...mapGetters([
      'loadingRegister',
      'registered',
      'errorRegister'
    ]),

    rules() {
      return {
        required: value => !!String(value || '').trim() || 'Pflichtfeld.',
        selectedAddress: () => !!this.selectedAddress || 'Bitte wählen Sie eine Adresse aus den Vorschlägen aus.',
        accepted: value => value === true || 'Bitte stimmen Sie den Bedingungen zu.',
        email: value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '') || 'Die E-Mail-Adresse ist ungültig.',
        zip: value => /^\d{5}$/.test(value || '') || 'Bitte geben Sie eine gültige fünfstellige PLZ ein.',
        phone: value => /^[+0-9][0-9\s/()-]{5,}$/.test(value || '') || 'Bitte geben Sie eine gültige Telefonnummer ein.',
        password: value => /^(?=.*[A-Za-z])(?=.*[0-9])(?=.*[~!@#$%^&*\-_+=|";[\]{}()<>,./?\\])/.test(value || '') ||
            'Das Passwort muss Buchstaben, Ziffern und ein Sonderzeichen enthalten.',
        passwordMin: value => String(value || '').length >= 9 || 'Das Passwort muss mindestens 9 Zeichen lang sein.',
        passwordMax: value => String(value || '').length <= 20 || 'Das Passwort darf maximal 20 Zeichen lang sein.',
        confirm: value => (
            value === this.registerForm.password &&
            this.registerForm.password !== ''
        ) || 'Die Passwörter müssen übereinstimmen.'
      }
    }
  },

  methods: {
    setState(state) {
      this.showModalRegister = state
    },

    showLegalConditions() {
      this.$refs.Legal.show({
        title: 'Nutzungsbedingungen',
        component: 'conditions'
      })
    },

    showLegalPrivacy() {
      this.$refs.Legal.show({
        title: 'Datenschutzerklärung',
        component: 'privacy'
      })
    },

    async registerUser() {
      const validation = await this.$refs.form.validate()

      if (!validation.valid || !this.selectedAddress) {
        return
      }

      await this.$store.dispatch('register', {
        ...this.registerForm,
        company: this.registerForm.customer === 'Gewerblich'
            ? this.registerForm.company
            : ''
      })
    },
    handleAddressInput(value) {
      this.selectedAddress = null
      this.registerForm.street = ''
      this.registerForm.house = ''
      this.registerForm.zip = ''
      this.registerForm.place = ''
      clearTimeout(this.addressSearchTimeout)

      if (!value || value.trim().length < 3) {
        this.addressSuggestions = []
        return
      }

      this.addressSearchTimeout = setTimeout(() => {
        this.fetchAddressSuggestions(value)
      }, 350)
    },
    async fetchAddressSuggestions(value) {
      this.addressLoading = true

      try {
        const results = await searchPhotonAddresses(value)
        this.addressSuggestions = results
            .filter(item => this.isCompleteAddress(item))
            .map(item => ({
              placeId: item.place_id,
              label: this.formatAddress(item),
              street: this.getAddressStreet(item.address),
              house: item.address?.house_number || '',
              zip: item.address?.postcode || '',
              place: this.getAddressCity(item.address)
            }))
      } catch (error) {
        this.addressSuggestions = []
      } finally {
        this.addressLoading = false
      }
    },
    selectAddress(suggestion) {
      this.selectedAddress = suggestion
      this.addressInput = suggestion.label
      this.addressSuggestions = []
      this.registerForm.street = suggestion.street
      this.registerForm.house = suggestion.house
      this.registerForm.zip = suggestion.zip
      this.registerForm.place = suggestion.place
    },
    getAddressStreet(address = {}) {
      return address.road || address.pedestrian || address.footway || address.residential || address.path || ''
    },
    getAddressCity(address = {}) {
      return address.city || address.town || address.village || address.municipality || ''
    },
    isCompleteAddress(item) {
      const address = item?.address || {}

      return !!(
          this.getAddressStreet(address) &&
          address.house_number &&
          this.getAddressCity(address) &&
          address.postcode
      )
    },
    formatAddress(item) {
      const address = item?.address || {}
      const streetWithHouse = `${this.getAddressStreet(address)} ${address.house_number}`.trim()

      return [
        streetWithHouse,
        [address.postcode, this.getAddressCity(address)].filter(Boolean).join(' '),
        address.country || 'Deutschland'
      ].filter(Boolean).join(', ')
    }
  },

  watch: {
    showModalRegister(newValue, oldValue) {
      if (!newValue && oldValue) {
        this.registerForm = emptyRegisterForm()
        this.addressInput = ''
        this.selectedAddress = null
        this.addressSuggestions = []
        this.showPassword = false
        this.showPasswordConfirmation = false
        this.$store.dispatch('resetErrorRegister')
        this.$store.dispatch('resetRegistered')
      }
    },

    'registerForm.customer'(value) {
      if (value !== 'Gewerblich') {
        this.registerForm.company = ''
      }
    }
  }
}
</script>
